// Parses the URL hash the backend uses to return from an SSO (Google/Microsoft) login.
//
// Possible shapes:
//   #token=<jwt>      — successful login, treat identically to the password-flow token
//   #sso_token=<jwt>  — login succeeded but 2FA is required; drives the existing 2FA flow
//   #<jwt>            — legacy/password-flow format without a key (kept for compatibility)
interface IParsedLoginHash {
  token?: string;
  ssoToken?: string;
}

/**
 * Extracts the token / sso_token returned by the backend in the URL hash after SSO login.
 * @param hash The raw `window.location.hash` value (with or without the leading `#`).
 * @returns `{ token }` for a successful login, `{ ssoToken }` when 2FA is required,
 *   or `{}` when the hash carries neither.
 */
export const parseLoginHash = (hash: string): IParsedLoginHash => {
  const raw = hash.startsWith('#') ? hash.substring(1) : hash;
  if (!raw) return {};

  // Legacy format: the whole hash is the token, with no key=value pair.
  if (!raw.includes('=')) {
    return { token: raw };
  }

  const params = new URLSearchParams(raw);
  const token = params.get('token') ?? undefined;
  const ssoToken = params.get('sso_token') ?? undefined;
  return { token, ssoToken };
};
